import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import type {ReactNode} from "react";
import {Modal} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {useHistory, useLocation} from "react-router-dom";
import {customLogEvent, isAndroid} from "FirebaseUtils";
import {PAGE_URLS} from "constants/AppConstants";
import {useAppUser} from "contexts/AppUserContext";
import PlanPickerCard from "components/Subscription/PlanPickerCard";
import {accountAgeInDays, fetchOnboardingPaywall, placementForUser, recordPaywallView} from "services/AdaptyWebService";
import {changeSubscriptionTier, startTrialCheckout} from "services/WebCheckoutService";
import type {CheckoutPlan, CheckoutTier} from "services/WebCheckoutService";
import {getBlockingStoreSubscription, getWebSubscriptionTier, hasSubscribedBefore} from "services/EntitlementService";
import {warmUpFunction} from "services/CloudFunctionWarmup";
import {preloadPaddle} from "services/PaddleCheckout";
import ToastService from "services/ToastService";

/**
 * The web paywall: one plan-picker modal for the whole app, so any Pro/Ultra
 * gate can open it instead of telling the user to go and subscribe elsewhere.
 *
 * Web only. Android opens the native Adapty paywall and the iOS webview asks the
 * host app through a WebKit message; both stay with their callers. The hook's
 * `openWebPaywall` is a no-op on Android so callers do not need to branch.
 *
 * Also owns the return from a paid checkout (`?checkout=success`, and the
 * `cancelled` an older Stripe redirect can still bring back) on every page but the
 * onboarding wizard, which runs its own flow.
 */

interface WebPaywallContextValue {
  openWebPaywall: (tier?: CheckoutTier) => void;
  isWebPaywallOpen: boolean;
}

const WebPaywallContext = createContext<WebPaywallContextValue>({
  openWebPaywall: () => undefined,
  isWebPaywallOpen: false
});

const TIERS: CheckoutTier[] = ["premium", "ultra"];

interface Props {
  children: ReactNode;
}

function WebPaywallProvider({children}: Props) {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const {appUser, user, fetchData} = useAppUser();

  const [isOpen, setIsOpen] = useState(false);
  const [tier, setTier] = useState<CheckoutTier>("premium");
  const [placementId, setPlacementId] = useState<string | undefined>();
  const [variationId, setVariationId] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Read once on load: the marker is stripped from the URL below, before the
  // signed-in user it needs is necessarily available.
  const [checkoutResult] = useState(() =>
    isAndroid ? null : new URLSearchParams(window.location.search).get("checkout")
  );
  const checkoutHandled = useRef(false);

  const refreshSubscription = useCallback(async () => {
    try {
      // fetchData(true) refreshes from Adapty and the biller server-side before it
      // re-reads the user, and publishes the new appUser to every consumer.
      await fetchData(true);
    } catch (error) {
      console.error("Error refreshing the subscription after checkout:", error);
    }
  }, [fetchData]);

  useEffect(() => {
    if (!checkoutResult || checkoutHandled.current || location.pathname === PAGE_URLS.onboardingPageUrl) {
      return;
    }
    if (checkoutResult === "cancelled") {
      checkoutHandled.current = true;
      customLogEvent("web_subscription_checkout_cancelled", {user: appUser?.uid, path: location.pathname});
    } else if (checkoutResult === "success") {
      if (!appUser?.uid) {
        return;
      }
      checkoutHandled.current = true;
      customLogEvent("web_subscription_checkout_success", {user: appUser.uid, path: location.pathname});
      ToastService.info(t("subscribe.checkoutSuccess"));
      setIsOpen(false);
      refreshSubscription();
    } else {
      checkoutHandled.current = true;
    }
    const params = new URLSearchParams(location.search);
    params.delete("checkout");
    const search = params.toString();
    history.replace({pathname: location.pathname, search: search ? `?${search}` : ""});
  }, [checkoutResult, appUser?.uid, location.pathname, location.search, history, refreshSubscription, t]);

  // Opening the picker is a paywall impression: resolve the placement for this
  // user (same rules as the native app), record the view, and keep the variation
  // so the resulting purchase can be attributed back to it.
  const openWebPaywall = useCallback(
    (requestedTier: CheckoutTier = "premium") => {
      if (isAndroid) {
        return;
      }
      const uid = appUser?.uid;
      if (!uid) {
        // The onboarding wizard doubles as a login and ends on the trial step.
        history.push(PAGE_URLS.onboardingPageUrl);
        return;
      }
      setTier(requestedTier);
      setIsOpen(true);
      customLogEvent("web_paywall_opened", {user: uid, tier: requestedTier, path: location.pathname});
      // Whichever of these the purchase will call cold-starts while the user picks a plan
      if (getWebSubscriptionTier(appUser)) {
        warmUpFunction("changeWebSubscriptionTier");
      } else {
        warmUpFunction("createPaddleCheckout");
        preloadPaddle();
      }

      (async () => {
        // Auth user, not appUser: only the former carries `metadata`.
        const placement = placementForUser(appUser?.userInDb?.subscription, accountAgeInDays(user));
        setPlacementId(placement);
        const paywall = await fetchOnboardingPaywall(uid, placement);
        if (paywall?.variationId) {
          setVariationId(paywall.variationId);
          recordPaywallView(paywall.variationId, uid);
        }
      })();
    },
    [appUser, user, history, location.pathname]
  );

  const close = useCallback(() => setIsOpen(false), []);

  const webTier = getWebSubscriptionTier(appUser);
  const webSubscription = webTier ? (appUser?.userInDb?.subscription as any)?.[webTier] : null;
  // Paddle cannot change plan mid-trial, so the change ends it and charges today.
  const changeEndsTrial = !!webSubscription?.isTrial && webSubscription?.store === "paddle";

  const handleSubscribe = async (chosenTier: CheckoutTier, plan: CheckoutPlan) => {
    // Only third-party-billed subscriptions block; a grant must not stop someone
    // from subscribing.
    if (getBlockingStoreSubscription(appUser)) {
      ToastService.error(t("subscribe.manageInStore"));
      return;
    }

    setIsSubmitting(true);
    if (webTier) {
      // Swap the price on the subscription they already have (prorated) instead
      // of creating a second one alongside it.
      await customLogEvent("web_subscription_tier_change", {user: appUser?.uid, tier: chosenTier, plan});
      const result = await changeSubscriptionTier(plan, chosenTier, variationId, placementId, changeEndsTrial);
      setIsSubmitting(false);
      if (result.success) {
        setIsOpen(false);
        ToastService.info(t("subscribe.tierChangeSuccess"));
        await refreshSubscription();
      } else if (result.reason === "cheaper-during-trial") {
        ToastService.error(t("subscribe.cheaperDuringTrial"));
      } else if (result.reason === "trial-ended-plan-unchanged") {
        ToastService.error(t("subscribe.trialEndedPlanUnchanged"));
        await refreshSubscription();
      } else {
        ToastService.error(t("subscribe.tierChangeError"));
      }
      return;
    }

    await customLogEvent("web_subscription_checkout_start", {
      user: appUser?.uid,
      tier: chosenTier,
      plan,
      path: location.pathname
    });
    const result = await startTrialCheckout(plan, {
      tier: chosenTier,
      variationId,
      placementId,
      // Back to the page the gate was on, so the unlocked feature is right there.
      returnPath: `${location.pathname}${location.search}`
    });
    if (result.cancelled) {
      setIsSubmitting(false);
      customLogEvent("web_subscription_checkout_cancelled", {user: appUser?.uid, path: location.pathname});
    } else if (!result.success) {
      setIsSubmitting(false);
      ToastService.error(t("subscribe.checkoutError"));
    }
    // On success the browser goes to `?checkout=success`, handled above.
  };

  const value = useMemo(() => ({openWebPaywall, isWebPaywallOpen: isOpen}), [openWebPaywall, isOpen]);

  return (
    <WebPaywallContext.Provider value={value}>
      {children}
      <Modal show={!isAndroid && isOpen} onHide={close} centered contentClassName="plan-picker-modal-content">
        <Modal.Body style={{padding: 0, display: "flex", justifyContent: "center"}}>
          <PlanPickerCard
            tiers={TIERS}
            initialTier={tier}
            trialEligible={!hasSubscribedBefore(appUser)}
            changingExisting={webTier !== null}
            changeEndsTrial={changeEndsTrial}
            upgradeBilledNow={webSubscription?.store === "paddle"}
            isSubmitting={isSubmitting}
            onSubscribe={handleSubscribe}
            onCancel={close}
            placementId={placementId}
            maxHeight="88vh"
          />
        </Modal.Body>
      </Modal>
    </WebPaywallContext.Provider>
  );
}

function useWebPaywall(): WebPaywallContextValue {
  return useContext(WebPaywallContext);
}

export {WebPaywallProvider, useWebPaywall};
